import * as React from "react";
import { getInitials, hashCode, warnDeprecation } from "../../utils/helpers";
import { cx } from "../../utils/dom";
import {
  BorderRadius,
  Color,
  ColorVariant,
  supportedColors,
} from "../../utils/theme";
import { useTheme } from "../theme";
import { StyleProps } from "../../utils/types";

type AvatarSize = "2xs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

type AvatarShape = "circle" | "square";

export interface AvatarProps
  extends React.ComponentProps<"div">,
    StyleProps<"base" | "fallback" | "img"> {
  src?: string;
  alt?: string;
  name?: string;
  fallback?: React.ReactNode;
  radius?: BorderRadius;
  color?: Color;
  variant?: ColorVariant;
  size?: AvatarSize;
  initialsLength?: number;
  /**@deprecated use radius instead */
  shape?: AvatarShape;
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      src,
      alt,
      name,
      color,
      variant = "light",
      fallback,
      children,
      size = "md",
      shape,
      className,
      radius = "full",
      classNames,
      styles,
      style,
      initialsLength = 1,
      ...props
    },
    ref
  ) => {
    const [imageError, setImageError] = React.useState(false);
    const theme = useTheme();
    const initials = React.useMemo(() => {
      if (!name) return "";
      return getInitials(name, initialsLength);
    }, []);

    function getAvatarRadius(): BorderRadius {
      warnDeprecation("shape", "radius", shape);
      // TODO: remove this when we remove the deprecated shape prop
      if (shape === "circle" && !radius) {
        radius = "full";
      }
      if (shape === "square" && !radius) {
        radius = "md";
      }
      return theme?.avatarRadiusSize || radius;
    }

    const defaultColor = React.useMemo(() => {
      const hash = hashCode(name || "");
      return supportedColors[hash % supportedColors.length];
    }, [name]);

    const renderContent = () => {
      if (src && !imageError) {
        return (
          <img
            className={cx(["avatar-img", classNames?.img])}
            onLoad={() => setImageError(false)}
            onError={() => setImageError(true)}
            src={src}
            alt={alt}
          />
        );
      }

      if (imageError && !children) {
        return fallback;
      }

      if (!src && !children && name) {
        return (
          <span
            style={styles?.fallback}
            className={cx(["avatar-fallback", classNames?.fallback])}
          >
            {initials}
          </span>
        );
      }

      return children;
    };

    return (
      <div
        style={{ ...style, ...styles?.base }}
        className={cx(["p-avatar", className, classNames?.base])}
        data-radius={getAvatarRadius()}
        data-color={color ?? defaultColor}
        data-variant={variant}
        data-interactive={false}
        data-size={size}
        ref={ref}
        {...props}
      >
        {renderContent()}
      </div>
    );
  }
);

Avatar.displayName = "Avatar";
